import type { HttpError } from '@refinedev/core'
import { useGetIdentity, useShow } from '@refinedev/core'
import type { PlatformUser } from '@sirius/contracts/admin'
import { useTranslation } from 'react-i18next'
import { ShowView, ShowViewHeader } from '~/components/refineUi/views/showView'
import { formatDateTime } from '~/i18n/format'
import { History } from '~/pages/activity/history'
import { NotFoundPage } from '~/pages/notFound'
import type { Identity } from '~/providers/authProvider'
import { AccountActions } from './accountActions'
import { MembershipsEditor } from './membershipsEditor'
import { UserRoleBadge, UserStatusBadge } from './userParts'

/**
 * One account: its profile, its actions (ban, staff), its memberships and its
 * history. Staff only.
 */
export function UserShow() {
  const { t } = useTranslation()
  const { query } = useShow<PlatformUser, HttpError>({ resource: 'users' })
  const user = query.data?.data
  const { data: viewer } = useGetIdentity<Identity>()

  if (query.error?.statusCode === 404) {
    return <NotFoundPage />
  }

  return (
    <ShowView>
      <ShowViewHeader title={user?.name} />
      {query.error && (
        <p
          role="alert"
          className="text-destructive text-sm"
        >
          {t('usersPage.show.loadError', { message: query.error.message })}
        </p>
      )}
      {user && (
        <div className="space-y-8">
          <dl className="grid max-w-md grid-cols-[8rem_1fr] gap-2 text-sm">
            <dt className="text-muted-foreground">
              {t('usersPage.show.name')}
            </dt>
            <dd>{user.name}</dd>
            <dt className="text-muted-foreground">
              {t('usersPage.show.email')}
            </dt>
            <dd>{user.email}</dd>
            <dt className="text-muted-foreground">
              {t('usersPage.show.role')}
            </dt>
            <dd>
              <UserRoleBadge platformRole={user.platformRole} />
            </dd>
            <dt className="text-muted-foreground">
              {t('usersPage.show.status')}
            </dt>
            <dd>
              <UserStatusBadge banned={user.banned} />
            </dd>
            {user.banned && (
              <>
                <dt className="text-muted-foreground">
                  {t('usersPage.show.bannedUntil')}
                </dt>
                <dd>
                  {user.banExpires === null
                    ? t('usersPage.show.noEnd')
                    : formatDateTime(user.banExpires)}
                </dd>
                <dt className="text-muted-foreground">
                  {t('usersPage.show.banReason')}
                </dt>
                <dd>{user.banReason ?? '—'}</dd>
              </>
            )}
            <dt className="text-muted-foreground">
              {t('usersPage.show.lastSignIn')}
            </dt>
            <dd>
              {user.lastSignInAt === null
                ? t('usersPage.never')
                : formatDateTime(user.lastSignInAt)}
            </dd>
            <dt className="text-muted-foreground">
              {t('usersPage.show.created')}
            </dt>
            <dd>{formatDateTime(user.createdAt)}</dd>
            <dt className="text-muted-foreground">
              {t('usersPage.show.updated')}
            </dt>
            <dd>{formatDateTime(user.updatedAt)}</dd>
          </dl>
          <AccountActions
            user={user}
            viewerId={viewer?.id}
          />
          <MembershipsEditor user={user} />
          {/* an account's events (bans, roles, passwords) and its
              memberships': staff-only platform events */}
          <History record={{ entity: 'user', id: user.id, tenantId: null }} />
        </div>
      )}
    </ShowView>
  )
}
